import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Text from '../../../components/Text/Text';
import Input from '../../../components/Input/Input';
import Alert from '../../../components/Alert/Alert';
import alertType from '../../../common/alertType';
import postRegistration from '../../../thunks/auth/registration';
import Button from '../../../components/Button/Button';
import routes from '../../../router/routes';

const Registration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    registration: {
      loading,
      error,
      registered
    }
  } = useSelector((state) => ({
    registration: state.registration
  }), shallowEqual);

  useEffect(() => {
    if (registered) {
      navigate(routes.LOGIN);
    }
  }, [registered]);

  const {
    values: {
      login,
      email,
      password,
      confirmPassword,
      name,
      lastName,
    },
    handleSubmit,
    handleChange,
    handleBlur,
    errors,
    isValid,
    touched
  } = useFormik({
    initialValues: {
      login: '',
      email: '',
      password: '',
      confirmPassword: '',
      name: '',
      lastName: '',
    },
    onSubmit: (values) => {
      dispatch(postRegistration({
        ...values,
        last_name: values.lastName
      }));
    },
    validationSchema: Yup.object().shape({
      login: Yup.string().required('Required').min(3, 'must be at least 5'),
      name: Yup.string().required('Required').min(3, 'must be at least 3'),
      lastName: Yup.string().required('Required').min(2, 'must be at least 2'),
      password: Yup.string().required('Required'),
      email: Yup.string().email('Invalid email').required('Required'),
      confirmPassword: Yup.string().required()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
    })
  });

  return (
    <div
      className="bg-background-light dark:bg-background-dark min-h-screen
      flex justify-center items-center px-[16px]"
    >
      <div
        className="w-full max-w-[450px] text-center p-[40px] rounded-[10px] shadowDefault
        bg-white dark:bg-[#1b263b] border border-transparent dark:border-[#415a77]/60"
      >
        <Text
          text="Create an Account"
          bold
          textStyle="text-[20px]"
          styleContainer="justify-center"
        />
        <Text muted text="Enter your details to register to your:" styleContainer="justify-center" />
        <div className="mb-[65px]" />
        <div>
          <div className="flex flex-col gap-3">
            <Input
              type="text"
              name="name"
              value={name}
              placeholder="First name"
              onBlur={handleBlur}
              onChange={handleChange}
              errorText={touched?.name && errors?.name}
            />
            <Input
              type="text"
              name="lastName"
              value={lastName}
              placeholder="Last name"
              onBlur={handleBlur}
              onChange={handleChange}
              errorText={touched?.lastName && errors?.lastName}
            />
            <Input
              type="text"
              name="login"
              value={login}
              placeholder="Login"
              onBlur={handleBlur}
              onChange={handleChange}
              errorText={touched?.login && errors?.login}
            />
            <Input
              type="text"
              name="email"
              value={email}
              placeholder="Email"
              onBlur={handleBlur}
              onChange={handleChange}
              errorText={touched?.email && errors?.email}
            />
            <Input
              type="password"
              name="password"
              value={password}
              onBlur={handleBlur}
              placeholder="Password"
              onChange={handleChange}
              errorText={touched?.password && errors?.password}
            />
            <Input
              type="password"
              name="confirmPassword"
              value={confirmPassword}
              onBlur={handleBlur}
              placeholder="Confirm Password"
              onChange={handleChange}
              errorText={touched?.confirmPassword && errors?.confirmPassword}
            />
          </div>
          <div className="flex flex-row justify-end">
            <a
              href={routes.LOGIN}
              className="text-[14px] font-semibold text-primary no-underline hover:text-primary-hover"
            >
              Sign in here
            </a>
          </div>
          <div className="flex flex-row justify-center">
            <Button
              text="Submit"
              disabled={!isValid || loading}
              onClick={handleSubmit}
            />
          </div>
          {error && (
            <Alert
              text={error}
              type={alertType.warning}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Registration;
